import React, { useEffect, useState } from "react";
import { Loader, Sparkles } from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import ResultSection from "../ResultSection";

function SparkColumn({ title, subtitle, color, items, testid }) {
  return (
    <div className="rm-card-static p-4" data-testid={testid}>
      <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-0.5"
           style={{ color, fontWeight: 700 }}>· {title}</div>
      <div className="font-display text-lg uppercase mb-3">{subtitle}</div>
      <ol className="text-sm space-y-2 list-none">
        {items.map((q, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-display text-xs" style={{ color }}>{String(i + 1).padStart(2, "0")}</span>
            <span>{q}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function SparksPanel() {
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sparks, setSparks] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/network/contacts");
        setContacts(data.contacts || []);
        if ((data.contacts || []).length > 0) {
          setSelectedId(data.contacts[0].id);
          if (data.contacts[0].sparks) setSparks(data.contacts[0].sparks);
        }
      } catch (e) { setError(friendlyAxiosError(e)); }
    })();
  }, []);

  const onSelect = (id) => {
    setSelectedId(id);
    const c = contacts.find((x) => x.id === id);
    setSparks(c?.sparks || null);
  };

  const generate = async () => {
    if (!selectedId) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/network/contacts/${selectedId}/sparks`);
      setSparks(data.sparks);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  if (contacts.length === 0) {
    return (
      <div className="rm-card-static p-6 text-center" data-testid="sparks-empty">
        <Sparkles size={36} style={{ color: "#FF6A00" }} className="mx-auto mb-2" />
        <div className="font-display text-xl uppercase">Add a stepping stone first.</div>
        <p className="text-sm mt-2" style={{ color: "#475569" }}>
          Sparks need someone to talk to. Head to Stepping Stones and add a contact.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="sparks-panel">
      <ResultSection
        title="Pick a contact"
        eyebrow="Conversation sparks generator"
        accentGradient="linear-gradient(135deg,#FF6A00 0%,#B45309 100%)"
        defaultOpen
        testid="sparks-picker-section"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="rm-label block mb-1">Pick a contact</label>
            <select className="rm-input" value={selectedId} onChange={(e) => onSelect(e.target.value)}
                    data-testid="sparks-contact-select">
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.role ? ` · ${c.role}` : ""}{c.company ? ` · ${c.company}` : ""}
                </option>
              ))}
            </select>
          </div>
          <button onClick={generate} disabled={loading} className="rm-btn rm-btn-primary text-xs px-3 py-2"
                  data-testid="sparks-generate-btn">
            {loading ? <Loader size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {loading ? "Conjuring…" : sparks ? "Re-generate (10 credits)" : "Generate sparks (10 credits)"}
          </button>
        </div>
        {error && <div className="mt-3 text-sm font-mono-rm" style={{ color: "#991B1B" }} data-testid="sparks-error">{error}</div>}
      </ResultSection>

      {sparks && (
        <>
          <ResultSection
            title="Openers"
            eyebrow="Open warm, open weird, open real"
            accentGradient="linear-gradient(135deg,#FFC145 0%,#CA8A04 100%)"
            count={(sparks.openers || []).length}
            defaultOpen
            testid="sparks-openers-section"
          >
            <SparkColumn title="Openers" subtitle="Open warm, open weird, open real." color="#FFC145"
                         items={sparks.openers || []} testid="sparks-openers" />
          </ResultSection>
          <ResultSection
            title="Deeper questions"
            eyebrow="Past surface, into substance"
            accentGradient="linear-gradient(135deg,#FF6A00 0%,#B45309 100%)"
            count={(sparks.deeper || []).length}
            testid="sparks-deeper-section"
          >
            <SparkColumn title="Deeper" subtitle="Past surface, into substance." color="#FF6A00"
                         items={sparks.deeper || []} testid="sparks-deeper" />
          </ResultSection>
          <ResultSection
            title="Reciprocity moves"
            eyebrow="Offer first. Always."
            accentGradient="linear-gradient(135deg,#10B981 0%,#047857 100%)"
            count={(sparks.reciprocity || []).length}
            testid="sparks-reciprocity-section"
          >
            <SparkColumn title="Reciprocity" subtitle="Offer first. Always." color="#10B981"
                         items={sparks.reciprocity || []} testid="sparks-reciprocity" />
          </ResultSection>
        </>
      )}
    </div>
  );
}
